/**
 * FMD Partner Hero — Flickity carousel + GreenLeaze price hydration
 * Custom element: <fmd-partner-hero>
 */
class FmdPartnerHero extends HTMLElement {
  connectedCallback() {
    this.flickity = null;
    this.priceTimeout = null;
    this.boundHydrate = this.hydrateGreenleazePrices.bind(this);

    this._fmdMonthlyAmount = parseFloat(this.dataset.fmdMonthlyAmount) || 0;
    this._discountType = this.dataset.fmdDiscountType;
    this._discountValue = parseFloat(this.dataset.fmdDiscountValue) || 0;

    this.waitForFlickity().then(() => this.initCarousel());
    this.initPriceHydration();
  }

  disconnectedCallback() {
    if (this.flickity) {
      this.flickity.destroy();
      this.flickity = null;
    }
    if (this.priceTimeout) {
      clearTimeout(this.priceTimeout);
    }
    window.removeEventListener('greenleazePriceActualize', this.boundHydrate);
  }

  waitForFlickity() {
    return new Promise(function (resolve) {
      if (window.Flickity) { resolve(); return; }
      var attempts = 0;
      var check = setInterval(function () {
        if (window.Flickity) { clearInterval(check); resolve(); }
        else if (++attempts > 100) { clearInterval(check); resolve(); }
      }, 100);
    });
  }

  initCarousel() {
    var track = this.querySelector('.fmd-hero__carousel');
    if (!track || !window.Flickity) return;

    this.flickity = new window.Flickity(track, {
      cellAlign: 'left',
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      wrapAround: false,
      draggable: true,
      accessibility: true,
      resize: true,
      percentPosition: false
    });

    var prevBtn = this.querySelector('.fmd-hero__carousel-btn--prev');
    var nextBtn = this.querySelector('.fmd-hero__carousel-btn--next');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.flickity.previous());
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.flickity.next());
    }

    this.flickity.on('change', () => {
      if (prevBtn) prevBtn.disabled = this.flickity.selectedIndex === 0;
      if (nextBtn) nextBtn.disabled = this.flickity.selectedIndex >= this.flickity.slides.length - 1;
    });

    if (prevBtn) prevBtn.disabled = true;
  }

  initPriceHydration() {
    window.addEventListener('greenleazePriceActualize', this.boundHydrate);

    if (typeof window.getAllPricesForProduct === 'function') {
      this.hydrateGreenleazePrices();
    } else {
      this.priceTimeout = setTimeout(() => {
        this.hidePriceLines();
      }, 3000);
    }
  }

  hydrateGreenleazePrices() {
    if (this.priceTimeout) {
      clearTimeout(this.priceTimeout);
      this.priceTimeout = null;
    }

    if (typeof window.getAllPricesForProduct !== 'function') return;

    var cards = this.querySelectorAll('[data-greenleaze-monthly]');
    var self = this;
    cards.forEach(function (card) {
      self.hydrateCard(card);
    });

    if (this.flickity) this.flickity.resize();
  }

  hydrateCard(card) {
    var priceTTC = parseInt(card.dataset.productPrice, 10);
    var taxable = card.dataset.productTaxable === 'true';
    var taxRate = taxable ? 1.2 : 1;
    var priceHT = (priceTTC / taxRate) / 100;

    var prices = window.getAllPricesForProduct(priceHT);
    if (!prices || !prices[36] || !prices[36].monthly || prices[36].monthly <= 0) {
      card.style.display = 'none';
      return;
    }

    card.style.display = '';

    var monthly = prices[36].monthly;
    var monthlySpan = card.querySelector('[data-greenleaze-monthly-value]');
    if (monthlySpan) {
      monthlySpan.textContent = Math.round(monthly) + ' € / mois';
    }

    var cardEl = card.closest('.fmd-hero__card');
    var fmdMonthly = this._fmdMonthlyAmount;
    if (this._discountType === 'percentage') {
      fmdMonthly = monthly * (this._discountValue / 100);
    }
    // FMD can't cover more than the monthly rental
    fmdMonthly = Math.min(fmdMonthly, monthly);

    var employerEl = cardEl.querySelector('[data-fmd-employer]');
    if (employerEl) {
      var greenSpan = employerEl.querySelector('.fmd-hero__card-line-green');
      if (greenSpan) {
        greenSpan.textContent = '-' + Math.round(fmdMonthly) + ' € / mois';
      }
    }

    var resteEl = cardEl.querySelector('[data-fmd-reste-a-charge]');
    if (resteEl) {
      var reste = Math.max(0, Math.round(monthly - fmdMonthly));
      var priceEl = resteEl.querySelector('.fmd-hero__card-reste-price');
      if (priceEl) {
        priceEl.innerHTML = reste + ' €<small> / mois</small>';
      }
    }

    var economyEl = cardEl.querySelector('[data-fmd-economy]');
    if (economyEl) {
      var bikePrice = Math.round(priceTTC / 100);
      var economyTotal = Math.min(Math.round(fmdMonthly * 36), bikePrice);
      var valueEl = economyEl.querySelector('.fmd-hero__card-fmd-value');
      if (valueEl && economyTotal > 0) {
        valueEl.textContent = economyTotal + ' €';
      }
    }
  }

  hidePriceLines() {
    var cards = this.querySelectorAll('[data-greenleaze-monthly]');
    cards.forEach(function (card) {
      card.style.display = 'none';
    });
    var restes = this.querySelectorAll('[data-fmd-reste-a-charge]');
    restes.forEach(function (el) {
      var priceEl = el.querySelector('.fmd-hero__card-reste-price');
      if (priceEl) priceEl.innerHTML = '- €<small> / mois</small>';
    });
  }
}

customElements.define('fmd-partner-hero', FmdPartnerHero);
